import { useTranslation } from "react-i18next"
import { describeReferenceVideoUrl } from "@nexus/contracts"
import { Icon } from "@nexus/ui"

import { ReferenceVideoPlatformMark } from "../ReferenceVideoPlatformMark"

/** One-line form for dense rows, such as the partner queue. */
export function OrderScriptReferenceVideoLink({ url }: { url: string | null | undefined }) {
  const { t } = useTranslation()
  const trimmed = url?.trim() ?? ""
  const source = describeReferenceVideoUrl(trimmed)
  if (!source) return null

  return (
    <a
      href={trimmed}
      target="_blank"
      rel="noopener noreferrer"
      // The row itself opens the upload panel; the link must only open the video.
      onClick={(event) => event.stopPropagation()}
      className="mt-1 inline-flex max-w-full items-center gap-1 rounded-sm text-[11px] font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
    >
      <ReferenceVideoPlatformMark platform={source.platform} className="size-3" />
      {t("marketplace.orders.list.drawer.referenceVideo", "Reference video")}
      <span className="truncate font-normal text-muted-foreground">{source.label}</span>
      <Icon name="external-link" size="xs" className="shrink-0" />
    </a>
  )
}

/** The link the client added on send for the partner to match. Nothing when there is none. */
export function OrderScriptReferenceVideo({ url }: { url: string | null | undefined }) {
  const { t } = useTranslation()
  const trimmed = url?.trim() ?? ""
  const source = describeReferenceVideoUrl(trimmed)
  if (!source) return null

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
        {t("marketplace.orders.list.drawer.referenceVideo", "Reference video")}
      </h3>
      <a
        href={trimmed}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-w-0 items-center gap-2 self-start rounded-md border border-border px-3 py-2 text-xs text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
      >
        <ReferenceVideoPlatformMark platform={source.platform} />
        <span className="shrink-0 font-medium">
          {source.handle ? `${source.label} · ${source.handle}` : source.label}
        </span>
        <span className="min-w-0 truncate text-muted-foreground">{trimmed}</span>
        <Icon name="external-link" size="sm" className="shrink-0 text-muted-foreground" />
        <span className="sr-only">
          {t("marketplace.sendHandoff.references.openLink", "Open link in a new tab")}
        </span>
      </a>
    </section>
  )
}
